import { z } from 'zod';
import {
  IdSchema,
  IsoDateTimeSchema,
  LogicalDataTypeSchema,
  MetadataSchema,
  PluginExtensionIdSchema,
  SchemaVersionSchema,
  SemanticTypeSchema,
  TimeGrainSchema,
} from './common.js';
import { ExpressionSchema, FormulaTextSchema } from './expression.js';
import { FormatSpecSchema } from './format.js';
import { AggregationSchema, DimensionRoleSchema, RelationshipSchema } from './semantic-model.js';

/**
 * Referencia al objeto fisico descubierto que motiva una recomendacion. No requiere
 * que exista ningun objeto del Semantic Model: apunta a la fuente, al objeto y,
 * opcionalmente, a la columna (con sus ids deterministas de descubrimiento).
 */
export const PhysicalRefSchema = z.strictObject({
  dataSourceId: IdSchema,
  namespace: z.array(z.string().min(1)).optional(),
  object: z.string().min(1),
  column: z.string().min(1).optional(),
  objectId: IdSchema.optional(),
  columnId: IdSchema.optional(),
});
export type PhysicalRef = z.infer<typeof PhysicalRefSchema>;

export const RECOMMENDATION_STATUSES = ['pending', 'accepted', 'rejected'] as const;
export const RecommendationStatusSchema = z.enum(RECOMMENDATION_STATUSES);
export type RecommendationStatus = z.infer<typeof RecommendationStatusSchema>;

/** `declared`: la fuente lo afirma (p. ej. una PK). `inferred`: heuristica, nunca un hecho. */
export const RECOMMENDATION_ORIGINS = ['declared', 'inferred'] as const;

export const EVIDENCE_SIGNALS = [
  'name',
  'native-type',
  'logical-type',
  'cardinality',
  'uniqueness',
  'nullability',
  'sample-values',
  'min-max',
  'pattern',
  'known-values',
  'format',
  'constraint',
  'metadata',
  'relationship',
] as const;
export const EvidenceSignalSchema = z.enum(EVIDENCE_SIGNALS);
export type EvidenceSignal = z.infer<typeof EvidenceSignalSchema>;

export const RecommendationEvidenceSchema = z.strictObject({
  signal: EvidenceSignalSchema,
  detail: z.string().min(1).max(500),
  /** Peso de la senal en la confianza final (0 a 1). */
  weight: z.number().min(0).max(1).optional(),
});
export type RecommendationEvidence = z.infer<typeof RecommendationEvidenceSchema>;

export const RecommendationReasonSchema = z.strictObject({
  code: z
    .string()
    .max(64)
    .regex(/^[a-z][a-z0-9-]*$/),
  message: z.string().min(1).max(2000),
  evidence: z.array(RecommendationEvidenceSchema).default([]),
});
export type RecommendationReason = z.infer<typeof RecommendationReasonSchema>;

export const GEO_SEMANTIC_TYPES = [
  'geo.country',
  'geo.region',
  'geo.state',
  'geo.city',
  'geo.postal-code',
  'geo.latitude',
  'geo.longitude',
  'geo.point',
] as const;

const base = {
  id: IdSchema,
  status: RecommendationStatusSchema.default('pending'),
  origin: z.enum(RECOMMENDATION_ORIGINS).default('inferred'),
  confidence: z.number().min(0).max(1),
  reason: RecommendationReasonSchema,
  sourceRefs: z.array(PhysicalRefSchema).min(1),
  /** Recomendaciones relacionadas (p. ej. la medida y su formato). */
  relatedIds: z.array(IdSchema).default([]),
  decidedAt: IsoDateTimeSchema.optional(),
  decidedBy: z.string().min(1).max(200).optional(),
};

const named = {
  name: z.string().min(1).max(200),
  label: z.string().min(1).max(200).optional(),
};

const recommendation = <const K extends string, P extends z.ZodType>(kind: K, proposal: P) =>
  z.strictObject({ ...base, kind: z.literal(kind), proposal });

/** Columnas por nombre fisico dentro del objeto de `sourceRefs[0]`. */
const columnList = z.array(z.string().min(1)).min(1);

const refIndex = z.number().int().nonnegative();

const builtinRecommendations = [
  recommendation(
    'dimension',
    z.strictObject({
      ...named,
      role: DimensionRoleSchema,
      dataType: LogicalDataTypeSchema,
      semanticType: SemanticTypeSchema.optional(),
      defaultTimeGrain: TimeGrainSchema.optional(),
    }),
  ),
  recommendation(
    'measure',
    z.strictObject({
      ...named,
      aggregation: AggregationSchema,
      dataType: LogicalDataTypeSchema.optional(),
      semanticType: SemanticTypeSchema.optional(),
      format: FormatSpecSchema.optional(),
    }),
  ),
  /** Las expresiones usan los ids de columna del descubrimiento; el PROMPT 04 los traduce. */
  recommendation(
    'calculatedMeasure',
    z.strictObject({
      ...named,
      expression: ExpressionSchema,
      formula: FormulaTextSchema.optional(),
      format: FormatSpecSchema.optional(),
    }),
  ),
  recommendation(
    'calculatedDimension',
    z.strictObject({
      ...named,
      role: DimensionRoleSchema,
      dataType: LogicalDataTypeSchema,
      expression: ExpressionSchema,
      formula: FormulaTextSchema.optional(),
    }),
  ),
  recommendation(
    'date',
    z.strictObject({
      grains: z.array(TimeGrainSchema).min(1),
      defaultTimeGrain: TimeGrainSchema.optional(),
      /** Candidata a fecha principal del modelo. */
      primaryCandidate: z.boolean().default(false),
      /** Patron CLDR detectado en los valores de texto (`dd/MM/yyyy`). */
      dateFormat: z.string().min(1).max(64).optional(),
    }),
  ),
  recommendation('geography', z.strictObject({ semanticType: z.enum(GEO_SEMANTIC_TYPES) })),
  recommendation('identifier', z.strictObject({ unique: z.boolean() })),
  recommendation('quantity', z.strictObject({ unit: z.string().min(1).max(32).optional() })),
  recommendation(
    'currency',
    z.strictObject({
      currency: z
        .string()
        .regex(/^[A-Z]{3}$/, 'Codigo ISO 4217')
        .optional(),
    }),
  ),
  recommendation('primaryKey', z.strictObject({ columns: columnList })),
  recommendation('unique', z.strictObject({ columns: columnList })),
  recommendation(
    'hierarchy',
    z.strictObject({
      ...named,
      /** De mayor a menor granularidad; cada nivel apunta a un indice de `sourceRefs`. */
      levels: z
        .array(
          z.strictObject({
            name: z.string().min(1).max(200),
            refIndex,
            timeGrain: TimeGrainSchema.optional(),
          }),
        )
        .min(2),
    }),
  ),
  recommendation(
    'relationship',
    z.strictObject({
      fromRefIndex: refIndex,
      toRefIndex: refIndex,
      cardinality: RelationshipSchema.shape.cardinality,
      crossFilter: z.enum(['single', 'both']).default('single'),
    }),
  ),
  recommendation('format', z.strictObject({ spec: FormatSpecSchema })),
] as const;

export const RECOMMENDATION_KINDS = builtinRecommendations.map(
  (schema) => schema.shape.kind.value,
) as readonly BuiltinRecommendationKind[];

/** Recomendacion aportada por un plugin (`plugin:<id>`), con propuesta libre sin secretos. */
const PluginRecommendationSchema = z.strictObject({
  ...base,
  kind: PluginExtensionIdSchema,
  proposal: MetadataSchema,
});

const refIndexIssues = (
  value: { kind: string; proposal: unknown; sourceRefs: readonly unknown[] },
  ctx: z.RefinementCtx,
): void => {
  const check = (index: number, path: (string | number)[]): void => {
    if (index >= value.sourceRefs.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['proposal', ...path],
        message: `El indice ${String(index)} no existe en sourceRefs.`,
      });
    }
  };
  if (value.kind === 'hierarchy') {
    const { levels } = value.proposal as { levels: readonly { refIndex: number }[] };
    levels.forEach((level, l) => {
      check(level.refIndex, ['levels', l, 'refIndex']);
    });
  }
  if (value.kind === 'relationship') {
    const { fromRefIndex, toRefIndex } = value.proposal as {
      fromRefIndex: number;
      toRefIndex: number;
    };
    check(fromRefIndex, ['fromRefIndex']);
    check(toRefIndex, ['toRefIndex']);
  }
};

/**
 * Recomendacion de modelado: interpretacion semantica POSIBLE de un objeto descubierto.
 * Nunca modifica el Semantic Model: aceptarla solo cambia `status`; la traduccion a
 * Dimension, Measure, Relationship... es una operacion posterior explicita.
 */
export const ModelingRecommendationSchema = z
  .union([z.discriminatedUnion('kind', builtinRecommendations), PluginRecommendationSchema])
  .superRefine(refIndexIssues);
export type ModelingRecommendation = z.infer<typeof ModelingRecommendationSchema>;
export type ModelingRecommendationInput = z.input<typeof ModelingRecommendationSchema>;
export type BuiltinRecommendationKind =
  (typeof builtinRecommendations)[number]['shape']['kind']['value'];
export type RecommendationKind = ModelingRecommendation['kind'];

/** Conjunto de recomendaciones generado a partir de uno o varios descubrimientos. */
export const ModelingRecommendationSetSchema = z
  .strictObject({
    schemaVersion: SchemaVersionSchema,
    id: IdSchema,
    dataSourceIds: z.array(IdSchema).min(1),
    discoveredAt: IsoDateTimeSchema,
    generatedAt: IsoDateTimeSchema,
    recommendations: z.array(ModelingRecommendationSchema),
  })
  .superRefine((set, ctx) => {
    const seen = new Set<string>();
    set.recommendations.forEach((item, i) => {
      if (seen.has(item.id)) {
        ctx.addIssue({
          code: 'custom',
          path: ['recommendations', i, 'id'],
          message: `Recomendacion duplicada "${item.id}".`,
        });
      }
      seen.add(item.id);
    });
  });
export type ModelingRecommendationSet = z.infer<typeof ModelingRecommendationSetSchema>;
export type ModelingRecommendationSetInput = z.input<typeof ModelingRecommendationSetSchema>;
